import { z } from 'zod/v4';

import { ComplianceStatusRuntype, RohsVersionRuntype } from '../backendTypes';
import { LifecycleEnum } from '../part/partBackendTypes';
import { ApiIntegrationStatusRuntype } from './3rdPartyBackendTypes';

const LifecycleEnumRuntype = z.enum(LifecycleEnum);

export interface SiliconExpertCredentialsDTO extends z.infer<typeof SiliconExpertCredentialsDTORuntype> {}
export const SiliconExpertCredentialsDTORuntype = z.object({
    username: z.string(),
    password: z.string(),
});

export interface SiliconExpertCredentialsInputDTO extends z.infer<typeof SiliconExpertCredentialsInputDTORuntype> {}
export const SiliconExpertCredentialsInputDTORuntype = z.object({
    username: z.string(),
    password: z.string(),
});

export interface SiliconExpertResponseBodyDTO extends z.infer<typeof SiliconExpertResponseBodyDTORuntype> {}
export const SiliconExpertResponseBodyDTORuntype = z.object({
    data: SiliconExpertCredentialsDTORuntype.nullable(),
    status: ApiIntegrationStatusRuntype,
});

export interface SiliconExpertQuotaInformationDTO extends z.infer<typeof SiliconExpertQuotaInformationDTORuntype> {}
export const SiliconExpertQuotaInformationDTORuntype = z.object({
    used_quota: z.number(),
    available_quota: z.number(),
    total_quota: z.number(),
    period: z
        .object({
            from: z.iso.date(),
            to: z.iso.date(),
        })
        .nullable(),
});

export interface AddPartsToAclRequestDTO extends z.infer<typeof AddPartsToAclRequestDTORuntype> {}
export const AddPartsToAclRequestDTORuntype = z.object({
    part_ids: z.array(z.string()),
});

export interface AclPartFeatureDTO extends z.infer<typeof AclPartFeatureDTORuntype> {}
export const AclPartFeatureDTORuntype = z.object({
    custFeatureName: z.string(),
    value: z.string(),
});

export interface AclPartFeaturesDTO extends z.infer<typeof AclPartFeaturesDTORuntype> {}
export const AclPartFeaturesDTORuntype = z.object({
    feature: z.array(AclPartFeatureDTORuntype),
});

export interface AclPartDTO extends z.infer<typeof AclPartDTORuntype> {}
export const AclPartDTORuntype = z.object({
    cpn: z.string().nullish(),
    com_id: z.string().nullish(),
    se_part_number: z.string().nullish(),
    se_manufacturer: z.string().nullish(),
    part_number: z.string().nullish(),
    manufacturer: z.string().nullish(),
    part_id: z.string().nullish(),
    features: AclPartFeaturesDTORuntype,
});

export interface AddPartsToAclResponseDTO extends z.infer<typeof AddPartsToAclResponseDTORuntype> {}
export const AddPartsToAclResponseDTORuntype = z.object({
    insertion_date: z.iso.date(),
    added_parts: z.array(AclPartDTORuntype),
});

export interface GetAclPartsSearchFilterDTO extends z.infer<typeof GetAclPartsSearchFilterDTORuntype> {}
export const GetAclPartsSearchFilterDTORuntype = z.object({
    cpn: z.string().optional(),
    mpn: z.string().optional(),
    man: z.string().optional(),
    com_id: z.string().optional(),
    page_number: z.number().optional(),
    page_size: z.number().optional(),
});

export interface GetAclPartsResponseDTO extends z.infer<typeof GetAclPartsResponseDTORuntype> {}
export const GetAclPartsResponseDTORuntype = z.object({
    result_size: z.number(),
    added_parts: z.array(AclPartDTORuntype),
});

export interface RemovePartFromAclQueryDTO extends z.infer<typeof RemovePartFromAclQueryDTORuntype> {}
export const RemovePartFromAclQueryDTORuntype = z.object({
    com_id: z.string(),
});

export interface RemovePartFromAclStatusDTO extends z.infer<typeof RemovePartFromAclStatusDTORuntype> {}
export const RemovePartFromAclStatusDTORuntype = z.object({
    com_id: z.string().optional(),
    part_id: z.string().optional(),
    delete_status: z.string(),
});

export interface RemovePartFromAclResponseDTO extends z.infer<typeof RemovePartFromAclResponseDTORuntype> {}
export const RemovePartFromAclResponseDTORuntype = z.object({
    deleted_parts: z.array(RemovePartFromAclStatusDTORuntype),
});

const SiliconExpertAuthorizedPartDTORuntype = z.object({
    part: z.uuid(),
    com_id: z.string(),
});

export const GetAuthorizedPartsResponseDTORuntype = z.object({
    parts: z.array(SiliconExpertAuthorizedPartDTORuntype),
});

export interface GetAuthorizedPartsResponseDTO extends z.infer<typeof GetAuthorizedPartsResponseDTORuntype> {}

const SiliconExpertAuthorizedPartDetailDTORuntype = z.object({
    part_id: z.uuid(),
    com_id: z.string().nullable(),
    user_id: z.uuid().nullable(),
    authorized_at: z.string(),
    quota_expires_at: z.iso.date(),
    is_expired: z.boolean(),
});

export const GetAuthorizedPartsDetailsResponseDTORuntype = z.object({
    parts: z.array(SiliconExpertAuthorizedPartDetailDTORuntype),
});

export interface GetAuthorizedPartsDetailsResponseDTO extends z.infer<
    typeof GetAuthorizedPartsDetailsResponseDTORuntype
> {}

export enum PcnType {
    Pcn = 'Pcn',
    Lifecycle = 'Lifecycle',
    Gidep = 'Gidep',
    Chemical = 'Chemical',
    Datasheet = 'Datasheet',
    Reach = 'Reach',
    ReachVersion = 'ReachVersion',
    Rohs = 'Rohs',
    RohsExemption = 'RohsExemption',
    SupplierAcquisition = 'SupplierAcquisition',
}

export const PcnTypeRuntype = z.enum(PcnType);

const RohsValueRuntype = z.object({
    value: ComplianceStatusRuntype.nullable(),
    version: RohsVersionRuntype.nullable(),
});

const PcnAffectedPartRuntype = z.object({
    com_id: z.string(),
    part_id: z.string(),
});

const PcnRecordPcnRuntype = z.object({
    pcn_type: z.literal(PcnType.Pcn),
    pcn_number: z.string().nullable(),
    pcn_type_name: z.string().nullable(),
    description: z.string().nullable(),
    effective_date: z.string().nullable(),
    url_source: z.string().nullable(),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordLifecycleRuntype = z.object({
    pcn_type: z.literal(PcnType.Lifecycle),
    new_status: LifecycleEnumRuntype.nullable(),
    old_status: LifecycleEnumRuntype.nullable(),
    ltb_date: z.string().nullable(),
    reason: z.string().nullable(),
    source: z.string().nullable(),
    source_type: z.string().nullable(),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordGidepRuntype = z.object({
    pcn_type: z.literal(PcnType.Gidep),
    gidep_document: z.string().nullable(),
    cage_code: z.string().nullable(),
    description: z.string().nullable(),
    gidep_alert_type: z.string().nullable(),
    gidep_document_date: z.string().nullable(),
    url_source: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordChemicalRuntype = z.object({
    pcn_type: z.literal(PcnType.Chemical),
    type_of_change: z.string().nullable(),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordDatasheetRuntype = z.object({
    pcn_type: z.literal(PcnType.Datasheet),
    type_of_change: z.string().nullable(),
    new_ds: z.string().nullable(),
    old_ds: z.string().nullable(),
    modification_date: z.string().nullable(),
    changed_features: z.array(z.string()),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordReachRuntype = z.object({
    pcn_type: z.literal(PcnType.Reach),
    new_value: ComplianceStatusRuntype.nullable(),
    old_value: ComplianceStatusRuntype.nullable(),
    datasheet: z.string().nullable(),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordReachVersionRuntype = z.object({
    pcn_type: z.literal(PcnType.ReachVersion),
    new_value: z.string().nullable(),
    old_value: z.string().nullable(),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordRohsRuntype = z.object({
    pcn_type: z.literal(PcnType.Rohs),
    new_value: RohsValueRuntype.nullable(),
    old_value: RohsValueRuntype.nullable(),
    datasheet: z.string().nullable(),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordRohsExemptionRuntype = z.object({
    pcn_type: z.literal(PcnType.RohsExemption),
    new_value: ComplianceStatusRuntype.nullable(),
    old_value: ComplianceStatusRuntype.nullable(),
    received_date: z.string().nullable(),
    affected_parts: z.array(PcnAffectedPartRuntype),
});

const PcnRecordSupplierAcquisitionRuntype = z.object({
    pcn_type: z.literal(PcnType.SupplierAcquisition),
    acquisition_type: z.string().nullable(),
    buyer: z.string().nullable(),
    date_of_acquisition: z.string().nullable(),
    details: z.string().nullable(),
    seller: z.string().nullable(),
    source: z.string().nullable(),
    received_date: z.string().nullable(),
});

export const PcnRecordRuntype = z.discriminatedUnion('pcn_type', [
    PcnRecordPcnRuntype,
    PcnRecordLifecycleRuntype,
    PcnRecordGidepRuntype,
    PcnRecordChemicalRuntype,
    PcnRecordDatasheetRuntype,
    PcnRecordReachRuntype,
    PcnRecordReachVersionRuntype,
    PcnRecordRohsRuntype,
    PcnRecordRohsExemptionRuntype,
    PcnRecordSupplierAcquisitionRuntype,
]);

export type PcnRecord = z.infer<typeof PcnRecordRuntype>;

export const GetPcnUpdatesRequestRuntype = z.object({
    from_date: z.string(),
    to_date: z.string(),
});

export const GetPcnUpdatesResponseRuntype = z.object({
    pcn_records: z.array(PcnRecordRuntype),
});

export interface GetPcnUpdatesResponse extends z.infer<typeof GetPcnUpdatesResponseRuntype> {}

export type QuotaGatedRawQuery = z.infer<typeof QuotaGatedRawQueryRuntype>;
export const QuotaGatedRawQueryRuntype = z.discriminatedUnion('raw_type', [
    z.object({ raw_type: z.literal('part_detail'), part_id: z.uuid() }),
    z.object({ raw_type: z.literal('geo_risk'), part_id: z.uuid() }),
    z.object({ raw_type: z.literal('supply_chain_events'), part_id: z.uuid() }),
    z.object({ raw_type: z.literal('pcn'), part_id: z.uuid() }),
    z.object({
        raw_type: z.literal('cross_references'),
        part_id: z.uuid(),
        page_number: z.number(),
        page_size: z.number(),
    }),
]);
